'use client'

import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useEffect, useMemo, useRef, useState } from 'react'
import { beginRouteTransition } from '@/lib/navigation-pending'
import { weatherBgUrl } from '@/lib/weather-bg'
import {
  cityPath,
  cityToSlug,
  foldCityKey,
  POPULAR_WEATHER_CITIES,
} from '@/lib/weather-city'
import { formatSiteDateTime, SITE_TIMEZONE } from '@/lib/site-datetime'

export type WeatherForecastHour = {
  time: string
  hourLabel: string
  temperature: number | null
  feelsLike: number | null
  humidity: number | null
  dewPoint?: number | null
  precipProb: number | null
  precip: number | null
  windSpeed: number | null
  windDir: string | null
  cloudCover: number | null
  uvIndex?: number | null
  visibility?: number | null
  isDay?: boolean | null
  condition: string | null
  icon: string | null
}

export type WeatherForecastDay = {
  date: string
  dateLabel: string
  dayLabel: string
  max: number | null
  min: number | null
  feelsLike: number | null
  feelsLikeMin?: number | null
  humidity: number | null
  precipProb: number | null
  precipSum?: number | null
  precipHours?: number | null
  windSpeed: number | null
  windGust?: number | null
  windDir: string | null
  cloudCover: number | null
  uvIndex?: number | null
  sunrise?: string | null
  sunset?: string | null
  condition: string | null
  icon: string | null
  summary: string | null
}

export type WeatherForecast = {
  city: string
  temperature: number | null
  feelsLike: number | null
  humidity: number | null
  dewPoint?: number | null
  windSpeed: number | null
  windGust?: number | null
  windDir: string | null
  precipProb: number | null
  precipSum?: number | null
  cloudCover: number | null
  pressure?: number | null
  visibility?: number | null
  uvIndex?: number | null
  isDay?: boolean | null
  sunrise?: string | null
  sunset?: string | null
  condition: string | null
  icon: string | null
  summary: string | null
  updatedAt?: string
  hours?: WeatherForecastHour[]
  days: WeatherForecastDay[]
}

/**
 * Site timezone — sv-SE güvenilir ISO üretir: "2026-08-02 08:14:33"
 * → key: 2026-08-02T08 (Open-Meteo hourly time ile aynı)
 */
function istanbulHourKey(d = new Date()): string | null {
  const raw = d.toLocaleString('sv-SE', { timeZone: SITE_TIMEZONE })
  const m = raw.match(/^(\d{4}-\d{2}-\d{2}) (\d{2}):/)
  if (!m) return null
  return `${m[1]}T${m[2]}`
}

type CityOption = { city: string }
type TabId = 'bugun' | 'yarin' | 'hafta' | '15gun'

const TABS: Array<{ id: TabId; label: string }> = [
  { id: 'bugun', label: 'Bugün' },
  { id: 'yarin', label: 'Yarın' },
  { id: 'hafta', label: 'Bu hafta' },
  { id: '15gun', label: '15 günlük' },
]

function formatTemp(n: number | null | undefined) {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—'
  return String(Math.round(n))
}

function formatUv(n: number | null | undefined) {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—'
  const v = Math.round(n * 10) / 10
  if (v < 3) return `${v} (Düşük)`
  if (v < 6) return `${v} (Orta)`
  if (v < 8) return `${v} (Yüksek)`
  if (v < 11) return `${v} (Çok yüksek)`
  return `${v} (Aşırı)`
}

function hasForecastData(f: WeatherForecast) {
  return (
    (typeof f.temperature === 'number' && Number.isFinite(f.temperature)) ||
    f.days.some((d) => typeof d.max === 'number' && Number.isFinite(d.max))
  )
}

function Stat({
  label,
  value,
  onMedia = false,
}: {
  label: string
  value: string
  onMedia?: boolean
}) {
  return (
    <div
      className={`rounded-md px-3 py-2 ${
        onMedia
          ? 'bg-white/15 text-white backdrop-blur-sm'
          : 'bg-white/80 dark:bg-slate-900/70'
      }`}
    >
      <div
        className={`text-[11px] ${
          onMedia ? 'text-white/75' : 'text-gray-500 dark:text-gray-400'
        }`}
      >
        {label}
      </div>
      <div
        className={`text-sm font-semibold tabular-nums ${
          onMedia ? 'text-white' : 'text-gray-900 dark:text-gray-100'
        }`}
      >
        {value}
      </div>
    </div>
  )
}

function TempRange({
  min,
  max,
  domainMin,
  domainMax,
}: {
  min: number | null
  max: number | null
  domainMin: number
  domainMax: number
}) {
  if (min == null || max == null || !Number.isFinite(min) || !Number.isFinite(max)) {
    return <div className="h-1.5 w-16 rounded-full bg-gray-100 dark:bg-slate-800" />
  }
  const span = Math.max(domainMax - domainMin, 1)
  const left = ((min - domainMin) / span) * 100
  const width = Math.max(((max - min) / span) * 100, 8)
  return (
    <div className="relative hidden h-1.5 w-16 overflow-hidden rounded-full bg-gray-100 sm:block dark:bg-slate-800">
      <div
        className="absolute inset-y-0 rounded-full bg-gradient-to-r from-sky-400 to-amber-400"
        style={{ left: `${left}%`, width: `${width}%` }}
      />
    </div>
  )
}

function DayRow({
  day,
  open,
  onToggle,
  domainMin,
  domainMax,
}: {
  day: WeatherForecastDay
  open: boolean
  onToggle: () => void
  domainMin: number
  domainMax: number
}) {
  return (
    <div className="border-b border-gray-100 last:border-0 dark:border-slate-800">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={`flex w-full items-center gap-3 px-3 py-3 text-left transition-colors sm:gap-4 sm:px-4 ${
          open
            ? 'bg-sky-50/70 dark:bg-slate-900/80'
            : 'hover:bg-gray-50 dark:hover:bg-slate-900/60'
        }`}
      >
        <div className="min-w-[5.5rem] shrink-0 sm:min-w-[7.5rem]">
          <div className="text-sm font-semibold text-gray-900 dark:text-white">{day.dayLabel}</div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400">{day.dateLabel}</div>
        </div>
        {day.icon ? (
          <Image
            src={`/weather-icons/${day.icon}`}
            alt={day.condition || ''}
            width={40}
            height={40}
            className="h-9 w-9 shrink-0 sm:h-10 sm:w-10"
          />
        ) : (
          <div className="h-9 w-9 shrink-0 sm:h-10 sm:w-10" />
        )}
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-gray-800 dark:text-gray-100">
            {day.condition || '—'}
          </div>
          <div className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
            Yağış %{formatTemp(day.precipProb)}
            {day.precipSum != null ? ` · ${day.precipSum.toFixed(1)} mm` : ''}
            {' · '}
            Rüzgar {day.windDir || '—'}{' '}
            {day.windSpeed != null ? `${day.windSpeed.toFixed(0)} km/s` : ''}
          </div>
        </div>
        <TempRange
          min={day.min}
          max={day.max}
          domainMin={domainMin}
          domainMax={domainMax}
        />
        <div className="shrink-0 text-right">
          <div className="text-base font-bold tabular-nums text-gray-900 dark:text-white">
            {formatTemp(day.max)}°
          </div>
          <div className="text-xs tabular-nums text-gray-500 dark:text-gray-400">
            {formatTemp(day.min)}°
          </div>
        </div>
        <span className="shrink-0 text-gray-400" aria-hidden>
          {open ? '▾' : '▸'}
        </span>
      </button>
      {open ? (
        <div className="space-y-3 bg-gray-50 px-3 py-3 text-sm dark:bg-slate-900/50 sm:px-4">
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            <Stat
              label="Hissedilen (max/min)"
              value={`${formatTemp(day.feelsLike)}° / ${formatTemp(day.feelsLikeMin)}°`}
            />
            <Stat label="Nem" value={day.humidity != null ? `%${Math.round(day.humidity)}` : '—'} />
            <Stat
              label="Yağış ihtimali"
              value={day.precipProb != null ? `%${Math.round(day.precipProb)}` : '—'}
            />
            <Stat
              label="Yağış miktarı"
              value={day.precipSum != null ? `${day.precipSum.toFixed(1)} mm` : '—'}
            />
            <Stat
              label="Yağışlı saat"
              value={day.precipHours != null ? `${Math.round(day.precipHours)} saat` : '—'}
            />
            <Stat
              label="Rüzgar / ani rüzgar"
              value={
                day.windSpeed != null
                  ? `${day.windDir || ''} ${day.windSpeed.toFixed(0)}${
                      day.windGust != null ? ` / ${day.windGust.toFixed(0)}` : ''
                    } km/s`.trim()
                  : '—'
              }
            />
            <Stat
              label="Bulut"
              value={day.cloudCover != null ? `%${Math.round(day.cloudCover)}` : '—'}
            />
            <Stat label="UV indeksi" value={formatUv(day.uvIndex)} />
            <Stat
              label="Gün doğumu / batımı"
              value={`${day.sunrise || '—'} / ${day.sunset || '—'}`}
            />
          </div>
          {day.summary ? (
            <p className="text-xs leading-relaxed text-gray-600 dark:text-gray-300">{day.summary}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export default function WeatherPageClient({
  initial,
  cities,
  citySlug,
}: {
  initial: WeatherForecast
  cities: CityOption[]
  citySlug?: string
}) {
  const router = useRouter()
  const pickerRef = useRef<HTMLDivElement>(null)
  const hourStripRef = useRef<HTMLDivElement>(null)
  const [query, setQuery] = useState('')
  const [pickerOpen, setPickerOpen] = useState(false)
  const [tab, setTab] = useState<TabId>('bugun')
  const [openDay, setOpenDay] = useState<string | null>(initial.days[0]?.date ?? null)
  // SSR’da yanlış TZ seçilmesin — sadece client’ta işaretle
  const [nowKey, setNowKey] = useState<string | null>(null)

  const hours = initial.hours ?? []
  const today = initial.days[0]
  const tomorrow = initial.days[1]
  const weekDays = initial.days.slice(0, 7)
  const activeSlug = citySlug || cityToSlug(initial.city)

  useEffect(() => {
    setTab('bugun')
    setOpenDay(initial.days[0]?.date ?? null)
    setNowKey(istanbulHourKey())
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset view when city changes
  }, [initial.city])

  useEffect(() => {
    setNowKey(istanbulHourKey())
    const id = window.setInterval(() => setNowKey(istanbulHourKey()), 30_000)
    return () => window.clearInterval(id)
  }, [])

  useEffect(() => {
    if (!pickerOpen) return
    const onPointer = (e: MouseEvent) => {
      if (!pickerRef.current?.contains(e.target as Node)) setPickerOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPickerOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [pickerOpen])

  useEffect(() => {
    if (tab !== 'bugun') return
    const el = hourStripRef.current?.querySelector<HTMLElement>('[data-now="1"]')
    el?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' })
  }, [tab, nowKey, initial.city, hours.length])

  const filtered = useMemo(() => {
    const q = foldCityKey(query)
    if (!q) return cities.slice(0, 60)
    return cities
      .filter((c) => {
        const key = foldCityKey(c.city)
        return key.includes(q) || cityToSlug(c.city).includes(q.replace(/\s+/g, '-'))
      })
      .slice(0, 60)
  }, [cities, query])

  const goCity = (city: string) => {
    setPickerOpen(false)
    setQuery('')
    const href = cityPath(city)
    beginRouteTransition(href)
    router.push(href)
  }

  const selectTab = (id: TabId) => {
    setTab(id)
    if (id === 'bugun') setOpenDay(today?.date ?? null)
    else if (id === 'yarin') setOpenDay(tomorrow?.date ?? null)
    else if (id === 'hafta') setOpenDay(today?.date ?? null)
    else setOpenDay(today?.date ?? null)
  }

  const listDays = useMemo(() => {
    if (tab === 'bugun') return today ? [today] : []
    if (tab === 'yarin') return tomorrow ? [tomorrow] : []
    if (tab === 'hafta') return weekDays
    return initial.days
  }, [tab, today, tomorrow, weekDays, initial.days])

  const hoursByDate = useMemo(() => {
    const map = new Map<string, WeatherForecastHour[]>()
    for (const h of hours) {
      const date = h.time.slice(0, 10)
      const list = map.get(date) ?? []
      list.push(h)
      map.set(date, list)
    }
    return map
  }, [hours])

  const stripHours = useMemo(() => {
    if (tab === 'yarin' && tomorrow?.date) {
      return (hoursByDate.get(tomorrow.date) ?? []).slice(0, 24)
    }
    if (today?.date) {
      const todayHours = hoursByDate.get(today.date) ?? []
      if (todayHours.length) return todayHours
    }
    return hours.slice(0, 24)
  }, [tab, tomorrow, today, hours, hoursByDate])

  const showHourStrip = (tab === 'bugun' || tab === 'yarin') && stripHours.length > 0
  const showWeekStrip = tab === 'hafta' || tab === '15gun'

  const { domainMin, domainMax } = useMemo(() => {
    const vals = initial.days.flatMap((d) => [d.min, d.max]).filter((n): n is number => n != null)
    if (!vals.length) return { domainMin: 0, domainMax: 30 }
    return { domainMin: Math.min(...vals), domainMax: Math.max(...vals) }
  }, [initial.days])

  const updatedLabel = initial.updatedAt
    ? formatSiteDateTime(initial.updatedAt, {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null

  const popular = POPULAR_WEATHER_CITIES
  const ready = hasForecastData(initial)

  const listTitle =
    tab === 'bugun'
      ? `${initial.city} Bugün`
      : tab === 'yarin'
        ? `${initial.city} Yarın`
        : tab === 'hafta'
          ? `${initial.city} Bu Hafta`
          : `${initial.city} 15 Günlük Hava Durumu`

  const nowBgUrl = weatherBgUrl(initial.icon)

  return (
    <div className="space-y-6">
      <nav className="text-xs text-gray-500 dark:text-gray-400" aria-label="Breadcrumb">
        <ol className="flex flex-wrap items-center gap-1.5">
          <li>
            <Link href="/" className="hover:text-red-600">
              Haber.com
            </Link>
          </li>
          <li aria-hidden>/</li>
          <li>
            <Link href="/hava-durumu" className="hover:text-red-600">
              Hava Durumu
            </Link>
          </li>
          <li aria-hidden>/</li>
          <li className="font-medium text-gray-800 dark:text-gray-200">{initial.city}</li>
        </ol>
      </nav>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-gray-900 dark:text-white sm:text-3xl">
            {initial.city} Hava Durumu
          </h1>
          {updatedLabel ? (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Son güncelleme: {updatedLabel}
            </p>
          ) : null}
        </div>

        <div className="relative w-full sm:max-w-xs" ref={pickerRef}>
          <label htmlFor="weather-city-search" className="sr-only">
            Şehir ara
          </label>
          <input
            id="weather-city-search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setPickerOpen(true)
            }}
            onFocus={() => setPickerOpen(true)}
            placeholder="Şehir ara"
            className="w-full rounded-md border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-[#C02727]/40 dark:border-slate-700 dark:bg-slate-900 dark:text-gray-100"
            autoComplete="off"
          />
          {pickerOpen ? (
            <ul className="absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-950">
              {filtered.length ? (
                filtered.map((c) => (
                  <li key={c.city}>
                    <button
                      type="button"
                      onClick={() => goCity(c.city)}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-red-50 dark:hover:bg-red-950/30 ${
                        c.city === initial.city
                          ? 'bg-red-50 font-semibold text-[#C02727] dark:bg-red-950/20'
                          : ''
                      }`}
                    >
                      {c.city}
                    </button>
                  </li>
                ))
              ) : (
                <li className="px-3 py-3 text-sm text-gray-500">Şehir bulunamadı</li>
              )}
            </ul>
          ) : null}
        </div>
      </div>

      {!ready ? (
        <section className="rounded-[5px] border border-amber-200 bg-amber-50 px-5 py-6 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-100">
          <p className="font-semibold">{initial.city} hava durumu şu an yüklenemedi.</p>
          <p className="mt-1 text-amber-800/90 dark:text-amber-200/80">
            Kısa süre sonra sayfayı yenileyin veya başka bir şehir seçin.
          </p>
        </section>
      ) : null}

      <section
        className="weather-hero-media relative overflow-hidden rounded-[5px] border border-white/20 bg-cover bg-center text-white shadow-sm"
        style={{ backgroundImage: `url('${nowBgUrl}')` }}
      >
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/45 to-black/30"
        />
        <div className="relative z-[1] grid gap-6 p-5 sm:grid-cols-[1.1fr_1fr] sm:p-7">
          <div className="flex items-start gap-4">
            {initial.icon ? (
              <Image
                src={`/weather-icons/${initial.icon}`}
                alt={initial.condition || ''}
                width={96}
                height={96}
                className="h-20 w-20 drop-shadow sm:h-24 sm:w-24"
                priority
              />
            ) : null}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-white/80">
                Şu an
              </p>
              <p className="mt-0.5 text-sm font-medium text-white">
                {initial.condition || '—'}
              </p>
              <p className="mt-1 text-5xl font-black tabular-nums tracking-tight text-white drop-shadow sm:text-6xl">
                {formatTemp(initial.temperature)}
                <span className="text-3xl">°</span>
              </p>
              <p className="mt-2 text-sm text-white/90">
                Hissedilen: {formatTemp(initial.feelsLike)}° · Bugün:{' '}
                {formatTemp(today?.max)}° / {formatTemp(today?.min)}°
              </p>
              {(initial.sunrise || initial.sunset) && (
                <p className="mt-1 text-xs text-white/75">
                  Gün doğumu {initial.sunrise || '—'} · Gün batımı{' '}
                  {initial.sunset || '—'}
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <Stat
              onMedia
              label="Nem"
              value={initial.humidity != null ? `%${Math.round(initial.humidity)}` : '—'}
            />
            <Stat
              onMedia
              label="Çiğ noktası"
              value={initial.dewPoint != null ? `${Math.round(initial.dewPoint)}°` : '—'}
            />
            <Stat
              onMedia
              label="Yağış ihtimali"
              value={
                initial.precipProb != null ? `%${Math.round(initial.precipProb)}` : '—'
              }
            />
            <Stat
              onMedia
              label="Yağış miktarı"
              value={
                initial.precipSum != null ? `${initial.precipSum.toFixed(1)} mm` : '—'
              }
            />
            <Stat
              onMedia
              label="Rüzgar"
              value={
                initial.windSpeed != null
                  ? `${initial.windDir || ''} ${initial.windSpeed.toFixed(0)} km/s`.trim()
                  : '—'
              }
            />
            <Stat
              onMedia
              label="Ani rüzgar"
              value={
                initial.windGust != null ? `${initial.windGust.toFixed(0)} km/s` : '—'
              }
            />
            <Stat
              onMedia
              label="Basınç"
              value={
                initial.pressure != null ? `${Math.round(initial.pressure)} hPa` : '—'
              }
            />
            <Stat
              onMedia
              label="Görüş"
              value={
                initial.visibility != null ? `${initial.visibility} km` : '—'
              }
            />
            <Stat
              onMedia
              label="Bulut oranı"
              value={
                initial.cloudCover != null ? `%${Math.round(initial.cloudCover)}` : '—'
              }
            />
            <Stat onMedia label="UV indeksi" value={formatUv(initial.uvIndex)} />
            <Stat
              onMedia
              label="Güneş"
              value={`${initial.sunrise || '—'} / ${initial.sunset || '—'}`}
            />
            <Stat
              onMedia
              label="Gündüz / gece"
              value={
                initial.isDay == null ? '—' : initial.isDay ? 'Gündüz' : 'Gece'
              }
            />
          </div>
        </div>
        {initial.summary ? (
          <div className="relative z-[1] border-t border-white/20 px-5 py-3 text-sm leading-relaxed text-white/90 sm:px-7">
            <strong className="text-white">Şu anda {initial.condition}</strong>
            <span className="mx-1.5 text-white/40">·</span>
            {initial.summary}
          </div>
        ) : null}
      </section>

      <div
        className="grid grid-cols-4 gap-1 rounded-[5px] border border-gray-200 bg-gray-50 p-1 dark:border-slate-700 dark:bg-slate-900/60"
        role="tablist"
        aria-label="Hava durumu görünümü"
      >
        {TABS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => selectTab(id)}
            className={`rounded-md px-2 py-2 text-center text-xs font-semibold transition-colors sm:text-sm ${
              tab === id
                ? 'bg-white text-[#C02727] shadow-sm dark:bg-slate-950'
                : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {showHourStrip ? (
        <section className="overflow-hidden rounded-[5px] border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-900/40">
          <div className="border-b border-gray-100 px-4 py-3 dark:border-slate-800">
            <h2 className="text-base font-bold text-gray-900 dark:text-white">
              {tab === 'yarin' ? 'Yarın — saatlik' : 'Bugün — saatlik'}
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Türkiye saati
              {tab === 'bugun' ? ' · vurgulanan dilim şu an' : ''}
            </p>
          </div>
          <div
            ref={hourStripRef}
            className="flex overflow-x-auto scroll-smooth"
          >
            {stripHours.map((h) => {
              const hourKey = h.time.slice(0, 13)
              const isNow = Boolean(nowKey && tab === 'bugun' && hourKey === nowKey)
              return (
                <div
                  key={h.time}
                  data-now={isNow ? '1' : undefined}
                  className={`flex w-[72px] shrink-0 flex-col items-center gap-1 border-r border-gray-100 px-1.5 py-3 last:border-r-0 sm:w-[76px] dark:border-slate-800 ${
                    isNow
                      ? 'bg-sky-50 ring-2 ring-inset ring-sky-400 dark:bg-sky-950/50 dark:ring-sky-500'
                      : 'bg-white dark:bg-transparent'
                  }`}
                  title={h.condition || undefined}
                >
                  <span
                    className={`text-[11px] font-semibold ${
                      isNow
                        ? 'text-sky-700 dark:text-sky-300'
                        : 'text-gray-600 dark:text-gray-300'
                    }`}
                  >
                    {isNow ? 'Şimdi' : h.hourLabel}
                  </span>
                  {h.icon ? (
                    <Image
                      src={`/weather-icons/${h.icon}`}
                      alt={h.condition || ''}
                      width={32}
                      height={32}
                      className="h-8 w-8"
                    />
                  ) : null}
                  <span className="text-sm font-bold tabular-nums text-gray-900 dark:text-white">
                    {formatTemp(h.temperature)}°
                  </span>
                  <span className="text-[10px] tabular-nums text-sky-600 dark:text-sky-400">
                    %{formatTemp(h.precipProb)}
                  </span>
                  <span className="text-[10px] tabular-nums text-gray-400">
                    {h.windSpeed != null ? `${h.windSpeed.toFixed(0)}` : '—'} km/s
                  </span>
                  {h.uvIndex != null && h.isDay !== false ? (
                    <span className="text-[10px] tabular-nums text-amber-600 dark:text-amber-400">
                      UV {Math.round(h.uvIndex)}
                    </span>
                  ) : null}
                </div>
              )
            })}
          </div>
        </section>
      ) : null}

      {showWeekStrip && weekDays.length > 0 ? (
        <section className="overflow-hidden rounded-[5px] border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-900/40">
          <div className="border-b border-gray-100 px-4 py-2.5 dark:border-slate-800">
            <h2 className="text-sm font-bold text-gray-900 dark:text-white">7 günlük özet</h2>
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-7">
            {weekDays.map((d) => {
              const active = openDay === d.date
              return (
                <button
                  key={d.date}
                  type="button"
                  onClick={() => {
                    setOpenDay(d.date)
                    if (tab === '15gun') {
                      /* keep 15-day list */
                    } else {
                      setTab('hafta')
                    }
                  }}
                  className={`flex flex-col items-center gap-1 border-r border-b border-gray-100 px-1 py-3 last:border-r-0 sm:border-b-0 dark:border-slate-800 ${
                    active
                      ? 'bg-sky-50 dark:bg-sky-950/40'
                      : 'hover:bg-gray-50 dark:hover:bg-slate-900'
                  }`}
                >
                  <span className="text-[11px] font-semibold text-gray-700 dark:text-gray-200 sm:text-xs">
                    {d.dayLabel}
                  </span>
                  {d.icon ? (
                    <Image
                      src={`/weather-icons/${d.icon}`}
                      alt=""
                      width={36}
                      height={36}
                      className="h-8 w-8 sm:h-9 sm:w-9"
                    />
                  ) : null}
                  <span className="text-sm font-bold tabular-nums text-gray-900 dark:text-white">
                    {formatTemp(d.max)}°
                  </span>
                  <span className="text-[11px] tabular-nums text-gray-500">
                    {formatTemp(d.min)}°
                  </span>
                  {d.uvIndex != null ? (
                    <span className="text-[10px] text-amber-600 dark:text-amber-400">
                      UV {Math.round(d.uvIndex)}
                    </span>
                  ) : null}
                </button>
              )
            })}
          </div>
        </section>
      ) : null}

      <section className="overflow-hidden rounded-[5px] border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-900/40">
        <div className="border-b border-gray-100 px-4 py-3 dark:border-slate-800">
          <h2 className="text-base font-bold text-gray-900 dark:text-white">{listTitle}</h2>
        </div>
        <div>
          {listDays.length ? (
            listDays.map((day) => (
              <DayRow
                key={day.date}
                day={day}
                open={openDay === day.date}
                onToggle={() => setOpenDay((prev) => (prev === day.date ? null : day.date))}
                domainMin={domainMin}
                domainMax={domainMax}
              />
            ))
          ) : (
            <p className="px-4 py-6 text-sm text-gray-500">Tahmin listesi bulunamadı.</p>
          )}
        </div>
      </section>

      <section>
        <h2 className="mb-3 text-sm font-bold text-gray-900 dark:text-white">
          Popüler iller
        </h2>
        <div className="flex flex-wrap gap-2">
          {popular.map((city) => {
            const href = cityPath(city)
            const active = activeSlug === cityToSlug(city) || initial.city === city
            return (
              <Link
                key={city}
                href={href}
                className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors sm:text-sm ${
                  active
                    ? 'border-sky-500 bg-sky-50 text-sky-800 dark:border-sky-500 dark:bg-sky-950/40 dark:text-sky-200'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-sky-300 dark:border-slate-700 dark:bg-slate-900 dark:text-gray-200'
                }`}
              >
                {city}
              </Link>
            )
          })}
        </div>
      </section>

      <section className="rounded-[5px] border border-gray-200 bg-white px-4 py-5 text-sm leading-relaxed text-gray-600 dark:border-slate-700 dark:bg-slate-900/40 dark:text-gray-300 sm:px-6">
        <h2 className="mb-2 text-base font-bold text-gray-900 dark:text-white">
          {initial.city} 15 Günlük Hava Durumu
        </h2>
        <p>
          {initial.city} hava durumu; saatlik sıcaklık, günlük UV, yağış miktarı, rüzgar/ani
          rüzgar, basınç, gün doğumu/batımı ve 15 günlük tahminleri içerir. Yarınki görünüm ve
          haftalık özet için üstteki sekmeleri kullanabilir, başka iller için şehir aramasından
          geçiş yapabilirsiniz.
        </p>
      </section>
    </div>
  )
}
